import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, API_BASE, apiGet, apiPost } from '../contexts/AuthContext';

function buildWsUrl(token) {
  const base = API_BASE || window.location.origin;
  const wsBase = base.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin' : '/partner';

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('xp:unread-count', { detail: unreadCount }));
  }, [unreadCount]);

  const wsRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null });

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiGet('/notifications/unread-count');
      setUnreadCount(res?.data?.unread || 0);
    } catch { /* ignore */ }
  }, [user]);

  const refreshList = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiGet('/notifications');
      setNotifications(Array.isArray(res?.data) ? res.data.slice(0, 8) : []);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    refreshUnreadCount();
    refreshList();
  }, [refreshUnreadCount, refreshList]);

  // Allow external components (like the Notifications page) to trigger a refresh
  useEffect(() => {
    const handleRefresh = () => {
      refreshUnreadCount();
      refreshList();
    };
    window.addEventListener('xp:refresh-notifications', handleRefresh);
    return () => window.removeEventListener('xp:refresh-notifications', handleRefresh);
  }, [refreshUnreadCount, refreshList]);

  // The list is otherwise only updated by the live WebSocket push, which can
  // miss items if the socket was reconnecting when something came in. Always
  // pull a fresh list when the dropdown is opened so it never shows stale
  // history instead of what actually happened most recently.
  useEffect(() => {
    if (open) {
      refreshList();
      refreshUnreadCount();
    }
  }, [open, refreshList, refreshUnreadCount]);

  // Live updates over the same WebSocket endpoint used for chat.
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let closedByUs = false;
    let pingTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(buildWsUrl(token));
        wsRef.current = ws;

        ws.onopen = () => {
          reconnectRef.current.attempts = 0;
          pingTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
            }
          }, 25000);
        };

        ws.onmessage = (evt) => {
          let data;
          try { data = JSON.parse(evt.data); } catch { return; }
          if (data?.type === 'notification' && data.notification) {
            const n = data.notification;
            setNotifications((prev) => [n, ...prev.filter((x) => x.id !== n.id)].slice(0, 8));
            setUnreadCount((prev) => prev + 1);
            // Chat messages already pop their own toast from the live chat
            // widget (with a jump-to-conversation action) — skip a second,
            // duplicate toast here. Still record it in the bell dropdown above.
            if (n.type === 'chat') return;
            if (n.type === 'loan_request') {
              toast(`New loan request from ${n.from_name || 'a partner'}`, {
                duration: 8000,
                description: `${n.candidate_name || 'A student'}${n.text ? ` · ${n.text}` : ''}`,
                onClick: () => navigate(`${basePath}/loans`),
                action: { label: 'Open', onClick: () => navigate(`${basePath}/loans`) },
              });
              return;
            }
            if (n.type === 'loan_status') {
              toast(`Loan application ${n.text}`, {
                duration: 5000,
                description: n.candidate_name ? `${n.candidate_name}'s education loan` : undefined,
                onClick: () => navigate(`${basePath}/loans`),
                action: { label: 'Open', onClick: () => navigate(`${basePath}/loans`) },
              });
              return;
            }
            if (n.type === 'message' || n.type === 'message_reply') {
              const title = n.type === 'message'
                ? `New message from ${n.from_name || 'Partner'}`
                : `Admin replied to your message`;
              toast(title, {
                duration: 5000,
                description: n.text ? (n.text.length > 80 ? n.text.slice(0, 80) + '…' : n.text) : undefined,
                onClick: () => navigate(`${basePath}/messages`),
                action: { label: 'Open', onClick: () => navigate(`${basePath}/messages`) },
              });
              return;
            }
            toast(n.type === 'comment' ? `${n.from_name ? n.from_name + ' commented' : 'New comment'}` : `${n.from_name || 'XpertAdvisor'} updated ${n.application_number || 'an application'}`, {
              duration: 5000,
              description: `${n.candidate_name ? `${n.candidate_name} — ` : ''}${(n.text || '').slice(0, 80)}`,
              onClick: () => navigate(`${basePath}/applications/${n.application_id}`),
              action: {
                label: 'Open',
                onClick: () => navigate(`${basePath}/applications/${n.application_id}`),
              },
            });
          }
        };

        ws.onclose = (event) => {
          clearInterval(pingTimer);
          pingTimer = null;
          wsRef.current = null;
          if (closedByUs) return;
          if (event.code === 4001 || event.code === 4003) return;
          const attempt = Math.min(reconnectRef.current.attempts + 1, 6);
          reconnectRef.current.attempts = attempt;
          const delay = Math.min(1000 * 2 ** (attempt - 1), 30000);
          reconnectRef.current.timer = setTimeout(connect, delay);
        };

        ws.onerror = () => {
          try { ws.close(); } catch { /* ignore */ }
        };
      } catch {
        reconnectRef.current.timer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      closedByUs = true;
      clearInterval(pingTimer);
      pingTimer = null;
      if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id || user?._id]);

  const handleOpenNotif = async (n) => {
    setOpen(false);
    if (!n.read) {
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      apiPost(`/notifications/${n.id}/read`, {}).catch(() => {});
    }
    if (n.type === 'chat') {
      // No dedicated page for live chat — open the floating chat widget
      // instead of navigating away from wherever the user currently is.
      window.dispatchEvent(new CustomEvent('xp:open-chat', { detail: { peerId: n.application_id } }));
      return;
    }
    if (n.type === 'loan_status' || n.type === 'loan_request') { navigate(`${basePath}/loans`); return; }
    if (n.type === 'message' || n.type === 'message_reply') { navigate(`${basePath}/messages`); return; }
    navigate(`${basePath}/applications/${n.application_id}`);
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        className="relative p-2 rounded-xl hover:bg-contrast/[0.07] transition-colors border border-transparent hover:border-contrast/10"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-ink-2" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-ember text-white dark:text-canvas text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="xp-notification-panel absolute right-0 top-full mt-2 w-80 bg-surface rounded-xl border border-contrast/[0.08] shadow-pop z-50 overflow-hidden" data-testid="notification-dropdown">
            <div className="px-4 py-3 border-b border-contrast/[0.07] flex items-center justify-between">
              <span className="text-sm font-semibold text-ink">Notifications</span>
              {unreadCount > 0 && <span className="text-xs text-brand font-medium">{unreadCount} unread</span>}
            </div>
            <div className="min-h-0 max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-ink-3">No notifications yet</div>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleOpenNotif(n)}
                    className={`w-full text-left flex items-start gap-2.5 px-4 py-3 border-b border-contrast/5 hover:bg-contrast/5 transition-colors ${!n.read ? 'bg-brand/[0.04]' : ''}`}
                  >
                    <MessageSquare className={`w-4 h-4 mt-0.5 shrink-0 ${n.read ? 'text-ink-3' : 'text-brand'}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs truncate ${n.read ? 'text-ink-2' : 'font-semibold text-ink'}`}>
                        {n.type === 'chat'
                          ? `${n.from_name || 'Someone'} sent a live chat message`
                          : n.type === 'loan_request'
                            ? `New loan request from ${n.from_name || 'a partner'} for ${n.candidate_name || 'a student'}`
                          : n.type === 'loan_status'
                            ? `Loan application for ${n.candidate_name || 'a candidate'} is now ${n.text}`
                            : n.type === 'message'
                              ? `New message from ${n.from_name || 'Partner'}: ${n.application_number || ''}`
                              : n.type === 'message_reply'
                                ? `Admin replied to: ${n.application_number || 'Message'}`
                                : `${n.from_name || 'Someone'} ${n.type === 'comment' ? 'commented on' : 'updated'} ${n.application_number || 'an application'}`}
                      </p>
                      <p className="text-xs text-ink-3 truncate">{n.text}</p>
                      <p className="text-[10px] text-ink-3 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => { setOpen(false); navigate(`${basePath}/notifications`); }}
              className="w-full text-center text-xs font-medium text-brand hover:bg-contrast/5 py-2.5 border-t border-contrast/[0.07]"
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );
}
